import { Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";
import {
  type EffortLevel,
  type ImpactLevel,
  LEVEL_LABELS,
} from "@/bizminer/App/PillarInsights/ImprovementLevers/shared/improvementLeverUtils.ts";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { bizminer_app_pillar_insight_improvement_lever_path } from "@/routes";
import type { BizminerImprovementLever } from "@/types/serializers";

interface ImprovementLeverSheetProps {
  lever: BizminerImprovementLever | null;
  pillarInsightId: number;
  onClose: () => void;
}

function formatCurrency(
  profit: { value: number; currency: string; description?: string } | null,
) {
  if (!profit || !profit.value) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: profit.currency || "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(profit.value);
}

const LEVEL_TO_SEGMENTS: Record<ImpactLevel | EffortLevel, number> = {
  low: 1,
  medium: 2,
  high: 3,
};

interface LevelProgressBarProps {
  level: ImpactLevel | EffortLevel;
  label: string;
}

function LevelProgressBar({ level, label }: LevelProgressBarProps) {
  const filledSegments = LEVEL_TO_SEGMENTS[level];

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-muted-foreground w-14">{label}</span>
      <div className="flex-1 flex gap-1">
        {[1, 2, 3].map((segment) => (
          <div
            key={segment}
            className={`h-2 flex-1 rounded-sm ${
              segment <= filledSegments
                ? "bg-primary"
                : "bg-muted-foreground/20"
            }`}
          />
        ))}
      </div>
      <span className="text-sm font-medium w-16 text-right">
        {LEVEL_LABELS[level]}
      </span>
    </div>
  );
}

interface ProfitIndicatorProps {
  value: number;
}

function ProfitIndicator({ value }: ProfitIndicatorProps) {
  const level = value >= 1000000 ? 3 : value >= 500000 ? 2 : 1;
  return (
    <div className="flex gap-1">
      {[1, 2, 3].map((dot) => (
        <div
          key={dot}
          className={`w-2 h-2 rounded-full ${
            dot <= level ? "bg-primary" : "bg-muted-foreground/30"
          }`}
        />
      ))}
    </div>
  );
}

export default function ImprovementLeverSheet({
  lever,
  pillarInsightId,
  onClose,
}: ImprovementLeverSheetProps) {
  return (
    <Sheet open={lever !== null} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto p-4"
      >
        {lever && (
          <>
            <SheetHeader>
              <SheetTitle>{lever.title}</SheetTitle>
              <SheetDescription>{lever.journey_stage}</SheetDescription>
            </SheetHeader>

            <Tabs defaultValue="overview" className="mt-6">
              <TabsList>
                <TabsTrigger value="overview">Summary</TabsTrigger>
                <TabsTrigger value="deep-dive">Deep Dive</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                      Key Metrics
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          Additional Profit
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">
                            {formatCurrency(lever.additional_profit)}
                          </span>
                          {lever.additional_profit?.value && (
                            <ProfitIndicator
                              value={lever.additional_profit.value}
                            />
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          Closing Period
                        </span>
                        <span className="text-sm font-semibold">
                          {lever.closing_period || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                      Classification
                    </h4>
                    <div className="space-y-3">
                      <LevelProgressBar
                        level={lever.impact as ImpactLevel}
                        label="Impact"
                      />
                      <LevelProgressBar
                        level={lever.effort as EffortLevel}
                        label="Effort"
                      />
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-card rounded-lg border-l-4 border-l-red-500">
                  <h4 className="text-sm font-semibold text-foreground mb-2">
                    Pain Point
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {lever.pain_point?.description ||
                      lever.pain_point?.title ||
                      "No pain point specified"}
                  </p>
                </div>

                <div className="p-4 bg-card rounded-lg border-l-4 border-l-green-500">
                  <h4 className="text-sm font-semibold text-foreground mb-2">
                    Solution
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {lever.solution?.description ||
                      lever.solution?.title ||
                      "No solution specified"}
                  </p>
                </div>

                {lever.affected_products &&
                  lever.affected_products.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">
                        Affected Products
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {lever.affected_products.map((product) => (
                          <span
                            key={product}
                            className="px-2 py-1 text-xs bg-muted rounded-md"
                          >
                            {product}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                {lever.functions_to_engage &&
                  lever.functions_to_engage.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">
                        Functions to Engage
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {lever.functions_to_engage.map((func) => (
                          <span
                            key={func}
                            className="px-2 py-1 text-xs bg-muted rounded-md"
                          >
                            {func}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                <div className="pt-4 border-t border-border">
                  <Link
                    href={bizminer_app_pillar_insight_improvement_lever_path(
                      pillarInsightId,
                      lever.id,
                    )}
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    View full details
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </TabsContent>

              <TabsContent value="deep-dive" className="mt-4">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  {lever.deep_dive ? (
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {lever.deep_dive}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      No deep dive content available.
                    </p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
